import {
  ButtonClean,
  ButtonPrimary,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  Select,
  Text
} from 'front-end/src/components'
import { t, Trans } from '@lingui/macro'
import React, { useState } from 'react'
import { brandColors } from 'shared/basicPalette'
import { DaySelector } from '../../../components/day-selector/DaySelector'
import { NbSp } from 'front-end/src/utils/NbSp'
import dayjs from 'shared/dayjs'
import {
  EventRecurrenceDayEnum,
  EventRecurrenceFreqEnum,
  EventRecurrencePattern
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { EventDetailEditFragment } from '../../EventEdit.codegen'
import { useManageEventContext } from '../../../ManageEventContext'
import { RecurrenceText } from 'front-end/src/pages/manage-events/components/recurrence-text/RecurrenceText'
import { getDateFormatBasedOnLocale } from 'front-end/src/components/common/date-time-pickers/dateTimePickersUtils'
import { createNewRRuleInstanceForPatternAndDate } from 'shared/recurringEventUtils'
import { notification } from 'front-end/src/components/common/notifications/Notifications'

const recurrenceScopeOpts = [
  {
    value: EventRecurrenceFreqEnum.DAILY,
    label: t`day`,
    labelPlural: t`days`
  },
  {
    value: EventRecurrenceFreqEnum.WEEKLY,
    label: t`week`,
    labelPlural: t`weeks`
  },
  {
    value: EventRecurrenceFreqEnum.MONTHLY,
    label: t`month`,
    labelPlural: t`months`
  }
]

export const getRecurrenceFreqByValue = (value: EventRecurrenceFreqEnum) => {
  return recurrenceScopeOpts.find((option) => option.value === value)
}

const MIN_REPEAT_VALUE = 1
const MAX_REPEAT_VALUE = 99

export const EventRecurrenceModal = ({
  resolve,
  reject,
  eventFormValues: values
}: {
  resolve: (state: EventRecurrencePattern) => void
  reject: () => void
  eventFormValues: EventDetailEditFragment
}) => {
  const { isRecurringSeries } = useManageEventContext()
  const startTime = dayjs(values.start_time)
  const accessor = isRecurringSeries
    ? 'recurrence'
    : 'recurringEvent.recurrence'
  const [recurrenceState, setRecurrenceState] =
    useState<EventRecurrencePattern>({
      freq: values[accessor]?.freq || EventRecurrenceFreqEnum.WEEKLY,
      interval: values[accessor]?.interval || 1,
      byDay: values[accessor]?.byDay || [
        Object.keys(EventRecurrenceDayEnum)[startTime.get('d')]
      ],
      count: values[accessor]?.count || 5
    })

  const changeRecurrenceState = (newParams: Partial<EventRecurrencePattern>) =>
    setRecurrenceState({ ...recurrenceState, ...newParams })

  const rule = createNewRRuleInstanceForPatternAndDate(
    startTime.toDate(),
    recurrenceState as any
  )

  const allOccurrences = rule.all()
  const lastOccurrence = dayjs(allOccurrences[allOccurrences.length - 1])

  const format = getDateFormatBasedOnLocale()

  return (
    <Modal
      newDesign
      header={t`Event series`}
      controls={
        <>
          <ButtonClean onClick={reject}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary
            onClick={() => {
              if (
                recurrenceState.freq === EventRecurrenceFreqEnum.WEEKLY &&
                recurrenceState.byDay?.length === 0
              ) {
                notification.warning(
                  t`Please select day for weekly recurrence.`
                )
                return
              }
              resolve({
                ...recurrenceState,
                byDay:
                  recurrenceState.freq === EventRecurrenceFreqEnum.WEEKLY
                    ? recurrenceState.byDay
                    : []
              })
            }}
          >
            <Trans>Done</Trans>
          </ButtonPrimary>
        </>
      }
      onHide={reject}
      dataTest="EventSeriesModal"
    >
      <FormControl as={Flex}>
        <FormLabel>
          <Trans>Repeat every</Trans>
        </FormLabel>

        <Input
          type="number"
          textAlign="center"
          onChange={(e) => {
            const newValue = Number(e.target.value)
            if (newValue > MAX_REPEAT_VALUE || newValue < MIN_REPEAT_VALUE) {
              return null
            }
            changeRecurrenceState({ interval: newValue })
          }}
          value={`${recurrenceState.interval}`}
          w="56px"
          pr={0}
          pl="16px"
          mr="8px"
          min={MIN_REPEAT_VALUE}
          max={MAX_REPEAT_VALUE}
          data-test="RepeatValueInput"
        />

        <Select
          options={recurrenceScopeOpts}
          onChange={(value) =>
            changeRecurrenceState({
              freq: value?.value
            })
          }
          value={getRecurrenceFreqByValue(
            recurrenceState.freq || EventRecurrenceFreqEnum.DAILY
          )}
          dataTest="RepeatTypeSelect"
        />
      </FormControl>

      {recurrenceState.freq === EventRecurrenceFreqEnum.WEEKLY && (
        <FormControl mt="16px">
          <FormLabel mb="8px">
            <Trans>Repeat on</Trans>
          </FormLabel>

          <DaySelector
            defaultValue={recurrenceState.byDay || []}
            onChange={(value) => changeRecurrenceState({ byDay: value })}
            dataTest="RepeatOnSelector"
          />
        </FormControl>
      )}

      <FormControl as={Flex} mt="16px" mb="8px">
        <FormLabel>
          <Trans>Ends after</Trans>
        </FormLabel>

        <Input
          type="number"
          textAlign="center"
          value={`${recurrenceState.count}`}
          onChange={(e) => {
            const newValue = Number(e.target.value)
            if (newValue > MAX_REPEAT_VALUE || newValue < MIN_REPEAT_VALUE) {
              return null
            }
            changeRecurrenceState({ count: newValue })
          }}
          w="56px"
          pr={0}
          pl="16px"
          mr="8px"
          min={MIN_REPEAT_VALUE}
          max={MAX_REPEAT_VALUE}
          data-test="EndsAfterValueInput"
        />

        <FormLabel>
          <Trans>occurrences</Trans>
        </FormLabel>
      </FormControl>

      <Text
        as="span"
        fontSize="sm"
        color={brandColors.grey['500']}
        data-test="RecurrenceUntilDate"
      >
        <RecurrenceText recurrence={recurrenceState} />
        <NbSp />
        <Trans>until {lastOccurrence.format(format)}</Trans>
      </Text>
    </Modal>
  )
}
